import { Smartlook, SmartlookResetSession, SmartlookSetupConfigBuilder, SmartlookUserIdentifier } from '@awesome-cordova-plugins/smartlook';
import { EnvConfiguration } from '../../app/config/EnvConfiguration.ts';
import { Device } from '../Device.ts';
import { Logger } from './Logger.ts';
import { TrackingStorageProvider } from '../tracking/TrackingStorageProvider.ts';

export class SmartlookService {
    private env: string | undefined;

    static inject = () => [EnvConfiguration, Logger, Device, TrackingStorageProvider];
    constructor(private config: EnvConfiguration, private log: Logger, private deviceInfo: Device, private storage: TrackingStorageProvider) {}

    private enabled = false;
    init = async ({ userId, isAdmin, name, email }: { userId: string, isAdmin: boolean, name: string, email: string  }) => {
        this.log.info('SmartlookService.init', { userId, email, name, isAdmin });

        try {
            if (!['ios', 'android'].includes(this.deviceInfo.platform)) {
                this.log.info(`Skipping smartlook init on platform "${this.deviceInfo.platform}"`);
                return;
            }

            const isRecording = await Smartlook.isRecording();
            if (isRecording) {
                this.log.info('Ending existing smartlook session');
                Smartlook.stopRecording();
                Smartlook.resetSession(new SmartlookResetSession(true));
            }

            if (this.storage.optOutOfTracking.get()){
                this.log.info(`Skipping smartlook init b/c tracking is globally opted out`);
                return;
            }

            if (!this.config.SMARTLOOK_API_KEY || process.env.NODE_ENV !== 'production') {
                this.log.info(`Skipping smartlook init for "${this.config.ENV}" env`);
                return;
            }

            if (this.env && this.env !== this.config.ENV) {
                // smartlook is unable to switch api keys once a recording has started
                // so after switching envs, user must restart app to continue recording
                this.log.info(`Skipping smartlook init after switching env from "${this.env}" to "${this.config.ENV}"`);
                return;
            }

            if (isAdmin) {
                this.log.info(`Skipping smartlook init for admin user`);
                return;
            }

            this.log.info(`Initializing smartlook for "${this.config.ENV}" env`);

            this.env = this.config.ENV;

            // NOTE: calling this a subsequent time starts a new recording, but does not update the api key
            // so after switching environments with a user that is being recorded, the app should be restarted,
            // otherwise the new recording will be created in the wrong project in smartlook
            const smartlookConfig = new SmartlookSetupConfigBuilder(this.config.SMARTLOOK_API_KEY).startNewSessionAndUser(true).build();

            Smartlook.setupAndStartRecording(smartlookConfig);
            Smartlook.setUserIdentifier(new SmartlookUserIdentifier(userId, { name, email }));

            this.enabled = true;

        } catch (e) {
            this.log.error('Failed to initialize smartlook', e);
        }
    };

    // we aren't sure this works, but by trying we'll find out :)
    updateUser = ({ userId, email, name }: { userId: string, email: string, name: string }) => {
        this.log.info('SmartlookService.updateUser', { userId, email, name, enabled: this.enabled });

        if (this.enabled){
            try{
                Smartlook.setUserIdentifier(new SmartlookUserIdentifier(userId, { email, name }));
            }
            catch(error: any){
                this.log.error(`failed to call Smartlook.setUserIdentifier. Got Error: ${error.toString()}`, { userId, name, email });
            }
        }
    };
}
